import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EmailPreviewDialog from "./EmailPreviewDialog";
import { useReportUnsaved } from "./unsaved-changes";
import {
  getAnnouncementData,
  previewAnnouncement,
  sendAnnouncement,
  sendAnnouncementTest,
} from "../services/api/announcements";
import { newUuid } from "../utils/uuid";

const LINK_PATTERN = /(https?:\/\/|www\.)\S+/gi;
const countLinks = (text) => (text.match(LINK_PATTERN) ?? []).length;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const when = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

/**
 * Manage League > Notifications > Announcements. An organizer writes a message and emails it to everyone
 * in the league. The server applies the real rules (who can send, how often, who is reached); this screen
 * shows them up front so nothing is a surprise.
 */
export default function AnnouncementComposer({ leagueId }) {
  const [data, setData] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");

  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  // One id per compose screen. Retrying or double-clicking Send reuses it, so the server sends only once.
  const [announcementId, setAnnouncementId] = React.useState(() => newUuid());

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null); // { tone: "success" | "info", text }
  const [preview, setPreview] = React.useState(null);
  const [confirming, setConfirming] = React.useState(false);

  const inputs = React.useRef({});
  const lastFocused = React.useRef("message");

  const refresh = React.useCallback(async () => {
    try {
      setData(await getAnnouncementData(leagueId));
      setLoadError("");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load announcements.");
    }
  }, [leagueId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // A draft that hasn't been sent. Reported before the early returns below, so the hook always runs.
  useReportUnsaved("announcement-draft", subject.trim() !== "" || message.trim() !== "", { label: "Announcement draft", scope: "tab" });

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!data) return null;

  const { limits, mergeFields, allowance, audience, history } = data;
  const links = countLinks(subject) + countLinks(message);
  const tooManyLinks = links > limits.maxLinks;
  const blockedByLimit = allowance.remaining <= 0;
  const noAudience = audience.willReceive === 0;
  const canSubmit = subject.trim() && message.trim() && !tooManyLinks && !blockedByLimit && !audience.tooMany && !noAudience;

  const insertField = (name) => {
    const field = lastFocused.current;
    const el = inputs.current[field];
    const token = `{{${name}}}`;
    const setter = field === "subject" ? setSubject : setMessage;
    const value = field === "subject" ? subject : message;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setter(value.slice(0, start) + token + value.slice(end));
    const caret = start + token.length;
    setTimeout(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    }, 0);
  };

  const run = async (action) => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onPreview = () =>
    run(async () => {
      const r = await previewAnnouncement(leagueId, { subject, message });
      if (!r.valid) {
        setError(r.errors.join(" "));
        return;
      }
      setPreview(r);
    });

  const onTest = () =>
    run(async () => {
      const r = await sendAnnouncementTest(leagueId, { subject, message });
      setResult({ tone: r.sent ? "success" : "info", text: r.message });
    });

  const onSend = () =>
    run(async () => {
      try {
        const r = await sendAnnouncement(leagueId, { id: announcementId, subject, message });
        setResult({
          tone: "success",
          text: `${r.alreadySent ? "That announcement was already sent. " : "Sent. "}${plural(r.queuedCount, "person", "people")} will get it within about 10 minutes${
            r.skippedCount > 0 ? `; ${plural(r.skippedCount, "person was", "people were")} skipped (opted out or unreachable)` : ""
          }.`,
        });
        setSubject("");
        setMessage("");
        setAnnouncementId(newUuid());
        await refresh();
      } finally {
        setConfirming(false);
      }
    });

  return (
    <Card variant="outlined" className="section-card" component="section" aria-labelledby="league-announcements-heading">
      <CardContent>
        <Stack spacing={1.5}>
          <Typography id="league-announcements-heading" variant="h5" component="h3">
            Announcements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email everyone in your league, for things like a rink change or a holiday schedule. Replies go to your Reply-To address above.
          </Typography>

          {audience.tooMany ? (
            <Alert severity="warning">
              This league has more than {limits.maxRecipients} people, which is more than one announcement can go to. Please contact support to send one.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
              This will reach {plural(audience.willReceive, "person", "people")} in {audience.total === 1 ? "your league" : `your league of ${audience.total}`}
              {audience.skipped > 0
                ? `. ${plural(audience.skipped, "person won't", "people won't")} get it because they turned off announcements, unsubscribed, or their email address doesn't work.`
                : "."}{" "}
              You can send {limits.perDay} per day and {limits.perWeek} per week.
            </Alert>
          )}

          {blockedByLimit ? (
            <Alert severity="warning">
              You've reached the announcement limit for now.
              {allowance.nextAllowedAt ? ` You can send another after ${when(allowance.nextAllowedAt)}.` : ""}
            </Alert>
          ) : null}

          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {mergeFields.map((f) => (
              <Tooltip key={f.name} title={`${f.description}. Example: ${f.example}`} arrow>
                <Chip label={`{{${f.name}}}`} size="small" variant="outlined" onClick={() => insertField(f.name)} disabled={busy} />
              </Tooltip>
            ))}
          </Stack>

          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onFocus={() => {
              lastFocused.current = "subject";
            }}
            inputRef={(el) => {
              inputs.current.subject = el;
            }}
            helperText={`${subject.length}/${limits.subject}`}
            error={subject.length > limits.subject}
          />
          <TextField
            label="Message"
            size="small"
            fullWidth
            multiline
            minRows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onFocus={() => {
              lastFocused.current = "message";
            }}
            inputRef={(el) => {
              inputs.current.message = el;
            }}
            error={message.length > limits.message || tooManyLinks}
            helperText={
              tooManyLinks
                ? `Please keep it to ${limits.maxLinks} links or fewer (you have ${links}).`
                : `Leave a blank line between paragraphs. ${message.length}/${limits.message}`
            }
          />
          <Typography variant="caption" color="text.secondary">
            Every email names you as the sender, says which league it is from, and includes an unsubscribe link. It is sent as plain text.
          </Typography>

          {error ? (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          ) : null}
          {result ? (
            <Alert severity={result.tone} onClose={() => setResult(null)}>
              {result.text}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="contained" onClick={() => setConfirming(true)} disabled={busy || !canSubmit}>
              {`Send to ${plural(audience.willReceive, "person", "people")}`}
            </Button>
            <Button variant="outlined" onClick={onTest} disabled={busy || !subject.trim() || !message.trim()}>
              Send me a test
            </Button>
            <Button variant="outlined" onClick={onPreview} disabled={busy || !subject.trim() || !message.trim()}>
              Preview
            </Button>
          </Stack>

          {history.length > 0 ? (
            <Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sent announcements
              </Typography>
              <Stack spacing={1} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {history.map((h) => (
                  <Box component="li" key={h.id}>
                    <Typography sx={{ fontWeight: 600 }}>{h.subject}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {when(h.createdAt)}
                      {h.sentBy ? ` by ${h.sentBy}` : ""} · {plural(h.queuedCount, "email", "emails")} sent
                      {h.skippedCount > 0 ? `, ${h.skippedCount} skipped` : ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}

          <EmailPreviewDialog
            open={Boolean(preview)}
            onClose={() => setPreview(null)}
            subject={preview?.subject}
            html={preview?.html}
            note="Shown as it would look to you. Each player's own name replaces the fields."
          />

          <Dialog open={confirming} onClose={() => (busy ? null : setConfirming(false))}>
            <DialogTitle>Send this announcement?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                &ldquo;{subject}&rdquo; will be emailed to {plural(audience.willReceive, "person", "people")}. This can&apos;t be unsent, and it counts toward your
                limit of {limits.perDay} per day.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirming(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="contained" onClick={onSend} disabled={busy}>
                Send
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </CardContent>
    </Card>
  );
}
